import React, { useState, useEffect, memo } from 'react';

import CrehanaLogo from 'crehana-icons/CrehanaLogo';
import { primaryColor } from 'crehana-ui/styles/colors';
import Text from 'crehana-ui/Text';

import ErrorBoundary from 'Shared/ErrorBoundaries/Simple';
import useMe from 'Shared/Hooks/useMe';

import getFeatureFlag from 'Utils/getFeatureFlag';
import loadScript from 'Utils/loadScript';

import { Inner } from './MainMenu.styles';
import mainMenuClassNames from '../styles/theme';
import getClassesByMode from '../styles/getClassesByMode';

import CookiesBar from '../components/CookiesBar';
import TopBanner from '../components/TopBanner';
import Right from '../components/Right';
import Left from '../components/Left';

import { MainMenuProps } from '../types';

const MainMenu: React.FC<MainMenuProps> = ({
  hideAll,
  countryPrefix,
  position = 'relative',
  categories,
  crehanaBaseUrl = '',
  mode = 'solid',
  // right props
  hideInboxNotifications,
  loginRegisterSwitcherProps,
  hideMembershipButton,
  hideShoppingCart,
  hideWishList,
  hideMyCoursesLink,
  hideModalLeave,
  hideAllRightLinks,
  hideComunityLink,
  showHubLinks,
  hideAffiliate,
  hideMembershipLink,
  blogCategories,
  // left props
  hideLeftLinks,
  customLogoColor,
  renderLogo,
  onClickLogo,
  hideSearchBar,
  // others
  showCookiesBanner = true,
  dontCheckForPagePromo,
  withoutTopBannerComponent,
  hideTopBanner,
  organization,
  innerCustomStyles,
  topBannerGraphqlVariables,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [showTopBannerInAllPages, setShowTopBannerInAllPages] = React.useState(
    false,
  );

  const { me: user, loading: loadingUser } = useMe();

  useEffect(() => {
    setIsClient(true);
    setShowTopBannerInAllPages(getFeatureFlag('enable_campaign_banner_pages'));
  }, []);

  useEffect(() => {
    if (!loadingUser && !user && getFeatureFlag('google_one_tab')) {
      loadScript({ src: 'https://accounts.google.com/gsi/client' });
    }
  }, [user, loadingUser]);

  const shouldRenderRightComponent = !hideAll && isClient;
  const orgSlug = organization ? organization.slug : '';
  const countryPrefixUrl = countryPrefix
    ? `/${countryPrefix.toLowerCase()}`
    : '';
  const isOrganization = Boolean(user && user.organization);
  const homeUserUrl = user ? `${crehanaBaseUrl}/home/` : `${countryPrefixUrl}/`;
  const crehanaLogoUrl =
    isOrganization && user && user.organization
      ? `${crehanaBaseUrl}/org/${user.organization.slug}`
      : homeUserUrl;

  const shouldShowTopBanner =
    !withoutTopBannerComponent && (showTopBannerInAllPages || !hideTopBanner);

  const getMainMenuLogo = () => {
    if (isOrganization) {
      if (user?.organization?.logoHeader) {
        return (
          <img
            src={user.organization.logoHeader}
            alt={user?.organization.name}
            className="h-32"
          />
        );
      }

      /* We only need to render this text if the organization did not set a custom logo header */
      return (
        <div className="flex flex-col items-center">
          <CrehanaLogo
            size={23}
            color={
              customLogoColor || (mode === 'solid' ? primaryColor : '#fff')
            }
          />
          <Text
            type="label"
            style={{ fontSize: '9px' }}
            color={mode === 'solid' ? 'gray-dark' : 'white'}
          >
            PARA EMPRESAS
          </Text>
        </div>
      );
    }

    return (
      <CrehanaLogo
        size={23}
        color={customLogoColor || (mode === 'solid' ? primaryColor : '#fff')}
      />
    );
  };

  return (
    <>
      {shouldShowTopBanner && (
        <ErrorBoundary>
          <TopBanner graphqlVariables={topBannerGraphqlVariables} />
        </ErrorBoundary>
      )}
      <nav className="relative w-full">
        <Inner
          className={`${mode} ${position} flex items-center justify-between left-0 right-0`}
          css={`
            z-index: 999;
            height: 52px;
            ${props => props.theme.breakpoints.up('md')} {
              height: 72px;
            }
          `}
          customStyles={innerCustomStyles}
        >
          <div
            className="block absolute md:relative z-7"
            css={`
              left: calc(50% - 62px);
              ${props => props.theme.breakpoints.up('md')} {
                left: inherit;
                margin-right: 12px;
              }
              @media (min-width: 1140px) {
                margin-right: 48px;
              }
            `}
          >
            {renderLogo ? (
              renderLogo()
            ) : (
              <a
                href={crehanaLogoUrl}
                className="block"
                aria-label="Inicio"
                onClick={onClickLogo || (() => {})}
              >
                {getMainMenuLogo()}
              </a>
            )}
          </div>
          {isClient && (
            <ErrorBoundary>
              <Left
                mode={mode}
                getClassesByMode={getClassesByMode}
                categories={categories}
                classes={mainMenuClassNames}
                hideLeftLinks={hideLeftLinks}
                crehanaBaseUrl={crehanaBaseUrl}
                countryPrefix={countryPrefix}
                blogCategories={blogCategories}
                showHubLinks={showHubLinks}
                hideSearchBar={hideSearchBar}
              />
            </ErrorBoundary>
          )}
          {shouldRenderRightComponent && (
            <ErrorBoundary>
              <Right
                mode={mode}
                getClassesByMode={getClassesByMode}
                countryPrefix={countryPrefix}
                crehanaBaseUrl={crehanaBaseUrl}
                hideInboxNotifications={hideInboxNotifications}
                loginRegisterSwitcherProps={loginRegisterSwitcherProps}
                hideMembershipButton={hideMembershipButton}
                hideShoppingCart={hideShoppingCart}
                hideWishList={hideWishList}
                hideMyCoursesLink={hideMyCoursesLink}
                hideModalLeave={hideModalLeave}
                hideAllRightLinks={hideAllRightLinks}
                hideComunityLink={hideComunityLink}
                showHubLinks={showHubLinks}
                hideAffiliate={hideAffiliate}
                hideMembershipLink={hideMembershipLink}
                blogCategories={blogCategories}
                dontCheckForPagePromo={dontCheckForPagePromo}
                orgSlug={orgSlug}
              />
            </ErrorBoundary>
          )}
        </Inner>
      </nav>
      {isClient && showCookiesBanner && <CookiesBar />}
    </>
  );
};

const propsAreEqual = (
  prevProps: MainMenuProps,
  nextProps: MainMenuProps,
): boolean => {
  return (
    prevProps.mode === nextProps.mode &&
    prevProps.position === nextProps.position &&
    prevProps.hideMembershipLink === nextProps.hideMembershipLink &&
    prevProps.hideSearchBar === nextProps.hideSearchBar &&
    prevProps.hideComunityLink === nextProps.hideComunityLink &&
    prevProps.customLogoColor === nextProps.customLogoColor &&
    prevProps.hideShoppingCart === nextProps.hideShoppingCart &&
    prevProps.hideTopBanner === nextProps.hideTopBanner &&
    prevProps.topBannerGraphqlVariables === nextProps.topBannerGraphqlVariables
  );
};

export default memo(MainMenu, propsAreEqual);
